import { z } from 'zod';

/**
 * Room-level companion PIP visibility (#4315).
 *
 * A host can take the companion's picture-in-picture tile off screen while the companion keeps
 * listening and talking. Stored under `workflows.active_configuration -> 'companionPip'`, beside
 * `music`, so the workflow row stays the one place a host's room-scoped companion choices live.
 * Absent means shown: rooms saved before the switch existed keep their bytes and their tile.
 * Rooms created from #4343 onward are stamped `DEFAULT_COMPANION_PIP_SETTING` instead of nothing,
 * so they start with the tile off without changing what absence means for everyone else.
 */
export const companionPipSettingSchema = z.object({ hidden: z.boolean() }).strict();
export type CompanionPipSetting = z.infer<typeof companionPipSettingSchema>;

/**
 * Room kinds that draw a companion tile a host can switch off. A talk show seats its companions in
 * the scene instead, and the settings write refuses any other kind — so `companionPip` is only ever
 * meaningful for these two, and writing it anywhere else would hide companions with no way back.
 */
export const COMPANION_PIP_ROOM_TYPES = ['standard', 'townhall'] as const;

/** Whether a room kind has a companion tile at all, and so whether `companionPip` means anything. */
export function roomTypeHasCompanionPip(roomType: string | null | undefined): boolean {
  return !!roomType && (COMPANION_PIP_ROOM_TYPES as readonly string[]).includes(roomType);
}

/**
 * What a newly created room's workflow is stamped with (#4343), for the kinds above only: the tile
 * starts off, and the host turns it on when they want it. Stamped at creation rather than folded
 * into the reader below, because the reader's absent-value default is what every room saved before
 * the switch existed relies on — flipping that would hide the tile in rooms whose hosts never
 * asked for it.
 */
export const DEFAULT_COMPANION_PIP_SETTING: CompanionPipSetting = { hidden: true };

/** Only an explicit `hidden: true` hides the tile; absent or malformed reads as shown. */
export function readCompanionPipHidden(configuration: unknown): boolean {
  const value = configuration && typeof configuration === 'object'
    ? (configuration as { companionPip?: unknown }).companionPip
    : undefined;
  return !!value && typeof value === 'object' && (value as { hidden?: unknown }).hidden === true;
}

/**
 * `room_notifications` message type published by the settings write. Signaling relays it to the
 * room as `SocketEvents.COMPANION_PIP_CHANGED`; the persisted value travels in the unified config.
 */
export const COMPANION_PIP_CHANGED_NOTIFICATION = 'companion_pip_changed' as const;

/** Payload of `SocketEvents.COMPANION_PIP_CHANGED`, sent to every socket in the room. */
export interface CompanionPipChangedPayload {
  roomId: string;
  hidden: boolean;
}
